// StreakComplete.js
// Streak Mode results screen. Shows the final streak, its quality tier, a share
// action (matching the new ROSTERABLE share template), and Play Again / Home.
// The score is already recorded by Streak.js before navigating here.

import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import wordmark from "../assets/Rosterable.png";
import lightningIcon from "../assets/lightning-fill.svg";
import { streakQuality } from "../managers/streakService";

const SPORT_EMOJI = {
  football: "🏈", baseball: "⚾", basketball: "🏀", hockey: "🏒", soccer: "⚽",
};

export default function StreakComplete() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};
  const { sport, finalScore } = state;
  const [shareLabel, setShareLabel] = useState("SHARE");
  const [hasAppeared, setHasAppeared] = useState(false);

  useEffect(() => {
    if (!sport) navigate("/");
  }, [sport, navigate]);
  useEffect(() => {
    const t = setTimeout(() => setHasAppeared(true), 60);
    return () => clearTimeout(t);
  }, []);

  if (!sport) return null;

  const score = finalScore || 0;
  const quality = streakQuality(score);

  async function handleShare() {
    const emoji = SPORT_EMOJI[sport.id] || "🏆";
    const text =
      `ROSTERABLE\n` +
      `${emoji} ${sport.name.toUpperCase()} STREAKS ${emoji}\n` +
      `Streak: ${score}\n\n` +
      `Daily roster building at Rosterableapp.com`;

    if (navigator.share && (!navigator.canShare || navigator.canShare({ text }))) {
      try {
        await navigator.share({ title: "Rosterable", text });
        return;
      } catch (err) {
        if (err && err.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        return;
      }
    }
    setShareLabel("COPIED!");
    setTimeout(() => setShareLabel("SHARE"), 1500);
  }

  return (
    <div style={styles.container}>
      {/* ── Header ── */}
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate("/")}>‹</button>
        <img src={wordmark} alt="Rosterable" style={styles.wordmark} />
        <div style={{ width: 44 }} />
      </div>

      {/* ── Title ── */}
      <div style={styles.titleRow}>
        <span style={{ ...styles.title, color: sport.hex }}>
          {sport.name.toUpperCase()} STREAK
        </span>
        <div style={{ ...styles.titleRule, background: `linear-gradient(to right, ${sport.hex}8c, transparent)` }} />
      </div>

      {/* ── Score ── */}
      <div style={styles.scoreSection}>
        <img src={lightningIcon} alt="" style={{ ...styles.scoreLightning, opacity: hasAppeared ? 1 : 0 }} />
        <span style={{
          ...styles.scoreNumber,
          color: sport.hex,
          transform: hasAppeared ? "scale(1)" : "scale(0.6)",
          opacity: hasAppeared ? 1 : 0,
          transition: "all 0.55s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}>
          {score}
        </span>
        <span style={styles.scoreCaption}>STREAK</span>
        <span style={{ ...styles.qualityPill, color: quality.color, borderColor: `${quality.color}80`, background: `${quality.color}1f` }}>
          {quality.label}
        </span>
      </div>

      <div style={{ flex: 1 }} />

      {/* ── Actions ── */}
      <button
        style={{ ...styles.primaryBtn, background: `linear-gradient(to right, ${sport.hex}, ${sport.hex}cc)`, boxShadow: `0 8px 22px ${sport.hex}59` }}
        onClick={() => navigate("/streak", { replace: true, state: { sport } })}
      >
        <img src={lightningIcon} alt="" style={styles.primaryIcon} />
        PLAY AGAIN
      </button>

      <div style={styles.secondaryRow}>
        <button style={styles.secondaryBtn} onClick={handleShare}>{shareLabel}</button>
        <button style={styles.secondaryBtn} onClick={() => navigate("/")}>HOME</button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    maxWidth: "480px",
    margin: "0 auto",
    padding: "0 16px 24px",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "52px",
    paddingBottom: "16px",
  },
  backBtn: {
    background: "none", border: "none", color: "rgba(255,255,255,0.8)",
    fontSize: "28px", fontWeight: "300", lineHeight: 1, padding: "4px 8px 4px 0", width: "44px", cursor: "pointer",
  },
  wordmark: { height: "21px", width: "auto" },

  titleRow: { display: "flex", alignItems: "center", gap: "10px", paddingTop: "6px", paddingBottom: "16px" },
  title: { fontSize: "19px", fontWeight: "500", letterSpacing: "2.5px", whiteSpace: "nowrap" },
  titleRule: { flex: 1, height: "1px" },

  scoreSection: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "6px",
    paddingTop: "48px",
  },
  scoreLightning: { width: "30px", height: "30px", filter: "brightness(0) invert(1)", opacity: 0.85, transition: "opacity 0.5s ease", marginBottom: "4px" },
  scoreNumber: { fontSize: "104px", fontWeight: "900", lineHeight: 1, letterSpacing: "-2px" },
  scoreCaption: { fontSize: "13px", fontWeight: "700", color: "rgba(255,255,255,0.5)", letterSpacing: "3px", marginTop: "4px" },
  qualityPill: {
    marginTop: "14px",
    fontSize: "14px",
    fontWeight: "800",
    letterSpacing: "1px",
    padding: "8px 18px",
    borderRadius: "999px",
    border: "1px solid",
  },

  primaryBtn: {
    width: "100%",
    border: "none",
    borderRadius: "14px",
    padding: "16px",
    color: "#fff",
    fontSize: "16px",
    fontWeight: "800",
    letterSpacing: "1px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "10px",
  },
  primaryIcon: { width: "18px", height: "18px", filter: "brightness(0) invert(1)" },
  secondaryRow: { display: "flex", gap: "10px" },
  secondaryBtn: {
    flex: 1,
    height: "48px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "rgba(255,255,255,0.8)",
    fontSize: "14px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    cursor: "pointer",
  },
};
